/* eslint-disable max-lines-per-function */
import { LocalDate, nativeJs } from '@js-joda/core';

import {
   PatientCheckEntitlementToMedicalServiceStatusCommand,
   PatientEntitlementToMedicalServiceStatusCheckResult,
} from '@dreryk/egabinet-patient-entitlement-frontend-api';
import {
   VisitCheckEntitlementToMedicalServiceStatusCommand,
   VisitCheckEntitlementToMedicalServiceStatusResult,
} from '@dreryk/egabinet-visit-frontend-api';
import {
   EwusAccountBlockedFault,
   EwusAccountLoginFault,
   EwusPasswordExpiredFault,
} from '@dreryk/egabinet-ewus-frontend-api';
import { PatientReferenceIdentifier } from '@dreryk/egabinet-patient-published-language-api';

import { useCqrsCommand } from '@dreryk/command-query-react-common';

import { ChildrenRendererProp, MergeProps, useMountEffect, useState } from '~libs.ui.react';
import { isNumber, isString, PromiseFunction, XOR } from '~libs.typescript';

import { EwusPasswordDaysUntilExpiration, PatientId, VisitId } from '../types';

import { useSetPatientEntitlementToMedicalServiceStatus } from './patientEntitlementToMedicalServiceStatusGlobalState';
import {
   PatientEntitlementToMedicalServiceStatusVerificationInternalState,
   PatientEntitlementToMedicalServiceStatusVerificationState,
} from './PatientEntitlementToMedicalServiceStatusVerification.types';
import {
   getCurrentPatientEntitlementToMedicalServicesStatus,
   getPatientEntitlementToMedicalServiceStatusVerificationState,
   getVerificationFailureEwusState,
} from './PatientEntitlementToMedicalServiceStatusVerification.utils';
import { PatientEntitlementToMedicalServiceStatus } from './patientEntitlementToMedicalServiceStatusGlobalState.types';

type PatientEntitlementToMedicalServiceStatusVerificationProps = MergeProps<
   [
      XOR<
         [
            { visitId: VisitId },
            {
               patientId: PatientId;
            }
         ]
      >,
      Required<
         ChildrenRendererProp<{
            verifyStatus: PromiseFunction;
            state: PatientEntitlementToMedicalServiceStatusVerificationState;
         }>
      >,
      {
         initialStatus: PatientEntitlementToMedicalServiceStatus;
         checkDate: Date;
         readonly?: boolean;
         patientReferenceIdentifier?: PatientReferenceIdentifier;
         verifyOnMount?: boolean;
         onEwusPasswordDaysUntilExpirationFault: (daysUntilExpiration: EwusPasswordDaysUntilExpiration) => void;
         onEwusPasswordExpiredFault: VoidFunction;
         onEwusLoginRequired: VoidFunction;
      }
   ]
>;

export const PatientEntitlementToMedicalServiceStatusVerification = (
   props: PatientEntitlementToMedicalServiceStatusVerificationProps
): JSX.Element => {
   const [state, setState] = useState<PatientEntitlementToMedicalServiceStatusVerificationInternalState>(
      getPatientEntitlementToMedicalServiceStatusVerificationState({
         checkDate: props.checkDate,
         patientReferenceIdentifier: props.patientReferenceIdentifier,
         entitlementStatementStatus: props.initialStatus.entitlementStatementStatus,
         ewusStatus: props.initialStatus.ewusStatus,
      })
   );
   const setPatientEntitlementToMedicalServiceStatus = useSetPatientEntitlementToMedicalServiceStatus();
   const verifyAvailable = !props.readonly && state.retryAvailable;

   const { executeAsync: verifyStatus, loading: verifing } = useCqrsCommand<
      PatientEntitlementToMedicalServiceStatusCheckResult | VisitCheckEntitlementToMedicalServiceStatusResult,
      void,
      EwusAccountLoginFault | EwusPasswordExpiredFault | EwusAccountBlockedFault
   >(
      () => {
         if ('patientId' in props) {
            const command = new PatientCheckEntitlementToMedicalServiceStatusCommand();
            command.patientId = props.patientId;
            command.checkDate = LocalDate.from(nativeJs(new Date(props.checkDate)));
            return command;
         }

         const command = new VisitCheckEntitlementToMedicalServiceStatusCommand();
         command.visitId = props.visitId;
         return command;
      },
      {
         onSuccess: ({
            daysUntilPasswordExpiration,
            patientEntitlementStatementStatus,
            patientEwusStatus,
            ewusLoginRequired,
         }) => {
            if (ewusLoginRequired) {
               return props.onEwusLoginRequired();
            }

            const ewusStatus = isString(patientEwusStatus) ? patientEwusStatus : patientEwusStatus.ewusStatus;

            if ('visitId' in props) {
               setPatientEntitlementToMedicalServiceStatus(props.visitId, {
                  entitlementStatementStatus: patientEntitlementStatementStatus,
                  ewusStatus: ewusStatus,
               });
            }

            if (isNumber(daysUntilPasswordExpiration)) {
               props.onEwusPasswordDaysUntilExpirationFault(daysUntilPasswordExpiration);
            }

            return setState(
               getPatientEntitlementToMedicalServiceStatusVerificationState({
                  checkDate: props.checkDate,
                  patientReferenceIdentifier: props.patientReferenceIdentifier,
                  entitlementStatementStatus: patientEntitlementStatementStatus,
                  ewusStatus: ewusStatus,
               })
            );
         },
         onFault: {
            'egabinet-ewus-frontend-api:EwusAccountLoginFault': () => {
               setState(
                  getVerificationFailureEwusState({
                     failureType: 'ACCOUNT_LOGIN_ERROR',
                  })
               );
            },
            'egabinet-ewus-frontend-api:EwusPasswordExpiredFault': () => {
               props.onEwusPasswordExpiredFault();
               setState(
                  getVerificationFailureEwusState({
                     failureType: 'PASSWORD_EXPIRED',
                  })
               );
            },
            'egabinet-ewus-frontend-api:EwusAccountBlockedFault': () => {
               setState(
                  getVerificationFailureEwusState({
                     failureType: 'ACCOUNT_BLOCKED',
                  })
               );
            },
         },
         onUnexpectedError: () => {
            setState(
               getVerificationFailureEwusState({
                  failureType: 'VERIFICATION_FAILURE',
               })
            );
         },
      }
   );

   useMountEffect(() => {
      if (props.verifyOnMount && verifyAvailable) {
         verifyStatus();
      }
   });

   return props.children({
      verifyStatus: verifyStatus,
      state: {
         status: getCurrentPatientEntitlementToMedicalServicesStatus(state),
         verifing: verifing,
         verifyAvailable: verifyAvailable,
      },
   });
};
